import axios from '@/plugins/axios';
import utils from '@/stores/utils';

const createState = () => ({
    customer: null,
    stock: null,

    stockEvolution: null,
    stockEvolutionPeriod: null,
    stockEvolutionLoading: false,
    stockEvolutionLoadingErrors: {},

    billingEvolution: null,
    billingEvolutionPeriod: null,
    billingEvolutionLoading: false,
    billingEvolutionLoadingErrors: {},

    activity: null,
    activityPeriod: null,
    activityLoading: false,
    activityLoadingErrors: {},

    statsLoading: false,
    statsLoadingErrors: {},

    lastMonth: null,
    previousMonth: null,
    lastMonthTrend: null,
    currentYear: null,
    lastYear: null,
    waitingValidation: null,
    waitingValidationInvoices: null,
    cancelTokenSource: null,
    cancelTokenSourceStats: null,
    cancelTokenSourceActivity: null,
    cancelTokenSourceStockEvolution: null,
    cancelTokenSourceBillingEvolution: null,
});

const mutations = {

    updateCustomer(state, customer) {
        state.customer = customer;
    },
    updateStatsLoading(state, statsLoading) {
        state.statsLoading = statsLoading;
        state.statsLoadingErrors = {};
    },
    updateStatsLoadingErrors(state, statsLoadingErrors) {
        state.statsLoadingErrors = statsLoadingErrors;
        state.statsLoading = false;
    },
    updateStock(state, stock) {
        state.stock = stock;
    },

    updateStats(state, stats) {
        state.statsLoading = false;
        state.lastMonth = stats.last_month_total_amount_excl_vat;
        state.previousMonth = stats.previous_month_total_amount_excl_vat;
        state.lastMonthTrend = stats.last_month_trend;
        state.currentYear = stats.current_year_total_amount_excl_vat;
        state.lastYear = stats.last_year_total_amount_excl_vat;
        state.waitingValidation = stats.draft_invoices_total_amount_excl_vat;
        state.waitingValidationInvoices = stats.draft_invoices_count;
    },

    updateStockEvolution(state, stockEvolution) {
        state.stockEvolution = stockEvolution;
        state.stockEvolutionLoading = false;
        state.stockEvolutionLoadingErrors = {};
    },
    updateStockEvolutionPeriod(state, stockEvolutionPeriod) {
        state.stockEvolution = null;
        state.stockEvolutionPeriod = stockEvolutionPeriod;
    },
    updateStockEvolutionLoading(state, stockEvolutionLoading) {
        state.stockEvolutionLoading = stockEvolutionLoading;
    },
    updateStockEvolutionLoadingErrors(state, stockEvolutionLoadingErrors) {
        state.stockEvolutionLoading = false;
        state.stockEvolutionLoadingErrors = stockEvolutionLoadingErrors;
    },

    updateBillingEvolution(state, billingEvolution) {
        state.billingEvolution = billingEvolution;
        state.billingEvolutionLoading = false;
        state.billingEvolutionLoadingErrors = {};
    },
    updateBillingEvolutionPeriod(state, billingEvolutionPeriod) {
        state.billingEvolution = null;
        state.billingEvolutionPeriod = billingEvolutionPeriod;
    },
    updateBillingEvolutionLoading(state, billingEvolutionLoading) {
        state.billingEvolutionLoading = billingEvolutionLoading;
    },
    updateBillingEvolutionLoadingErrors(state, billingEvolutionLoadingErrors) {
        state.billingEvolutionLoading = false;
        state.billingEvolutionLoadingErrors = billingEvolutionLoadingErrors;
    },


    updateActivity(state, activity) {
        state.activity = activity;
        state.activityLoading = false;
        state.activityLoadingErrors = {};
    },
    updateActivityPeriod(state, activityPeriod) {
        state.activity = null;
        state.activityPeriod = activityPeriod;
    },
    updateActivityLoading(state, activityLoading) {
        state.activityLoading = activityLoading;
    },
    updateActivityLoadingErrors(state, activityLoadingErrors) {
        state.activityLoading = false;
        state.activityLoadingErrors = activityLoadingErrors;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateCancelTokenSourceStats(state, source) {
        state.cancelTokenSourceStats = source;
    },
    updateCancelTokenSourceActivity(state, source) {
        state.cancelTokenSourceActivity = source;
    },
    updateCancelTokenSourceStockEvolution(state, source) {
        state.cancelTokenSourceStockEvolution = source;
    },
    updateCancelTokenSourceBillingEvolution(state, source) {
        state.cancelTokenSourceBillingEvolution = source;
    },
};

const actions = {

    async fetchStats({ commit, dispatch, state }, params) {
        // Cancel any pending request
        if (state.cancelTokenSourceStats) {
            state.cancelTokenSourceStats.cancel("New stats fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSourceStats', source);

        commit('updateStatsLoading', true);
        try {
            const response = await axios.get('/api/stats/billing/stats/', {
                params: { entity: state.customer.id },
                cancelToken: source.token,
            });
            commit('updateStats', response.data);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateStatsLoadingErrors', error.details);
            throw error;
        }
    },

    async fetchActivity({ commit, dispatch, state }, params) {
        // Cancel any pending request
        if (state.cancelTokenSourceActivity) {
            state.cancelTokenSourceActivity.cancel("New activity fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSourceActivity', source);

        commit('updateActivityLoading', true);
        try {
            const response = await axios.get('/api/stats/customer/activity/', {
                params: { customer: state.customer.id, start: state.activityPeriod.start, end: state.activityPeriod.end },
                cancelToken: source.token,
            });
            commit('updateActivity', response.data);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateActivityLoadingErrors', error.details);
            throw error;
        }
    },

    setActivityPeriod({ commit, dispatch, state }, params) {
        commit('updateActivityPeriod', params);
        dispatch('fetchActivity');
    },

    async fetchStockEvolution({ commit, dispatch, state }, params) {
        // Cancel any pending request
        if (state.cancelTokenSourceStockEvolution) {
            state.cancelTokenSourceStockEvolution.cancel("New stock evolution fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSourceStockEvolution', source);

        commit('updateStockEvolutionLoading', true);
        try {
            const response = await axios.get('/api/stats/stock/evolution/', {
                params: { customer: state.customer.id, start: state.stockEvolutionPeriod.start, end: state.stockEvolutionPeriod.end, stock: state.stock },
                cancelToken: source.token,
            });
            commit('updateStockEvolution', response.data);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateStockEvolutionLoadingErrors', error.details);
            throw error;
        }
    },
    setStockEvolutionPeriod({ commit, dispatch, state }, params) {
        commit('updateStockEvolutionPeriod', params);
        dispatch('fetchStockEvolution');
    },

    async fetchBillingEvolution({ commit, dispatch, state }, params) {
        // Cancel any pending request
        if (state.cancelTokenSourceBillingEvolution) {
            state.cancelTokenSourceBillingEvolution.cancel("New billing evolution fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSourceBillingEvolution', source);

        commit('updateBillingEvolutionLoading', true);
        try {
            const response = await axios.get('/api/stats/billing/evolution/', {
                params: { entity: state.customer.id, start: state.billingEvolutionPeriod.start, end: state.billingEvolutionPeriod.end },
                cancelToken: source.token,
            });
            commit('updateBillingEvolution', response.data);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateBillingEvolutionLoadingErrors', error.details);
            throw error;
        }
    },
    setBillingEvolutionPeriod({ commit, dispatch, state }, params) {
        commit('updateBillingEvolutionPeriod', params);
        dispatch('fetchBillingEvolution');
    },
    changeStock({ commit, dispatch, state }, stock) {
        commit('updateStock', stock);
        dispatch('fetchStockEvolution');
    },


};

export default () => ({
    namespaced: true,
    state: createState(),
    mutations,
    actions
});